import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { DeleteOutline, SearchOutlined, FileCopy } from '@material-ui/icons';
import CustomersIcon from '@material-ui/icons/GroupOutlined';
import { Button } from '@mui/material';
import { Stack } from '@mui/system';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import * as React from "react";
import CustomerContractIconCustom from '../CustomerContractIconCustom';
import { ICustomerContractServiceInDTO } from '../../repository/jago/model/input/ICustomerContractServiceInDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import translations from '../../translations/i18next';
import Utils, { getCustomerContractServiceStatusColor } from '../../utils/Utils';
import ReduxLanguage from '../ReduxLanguage';

const styles = createStyles({
  rowContainer: {
    display: 'flex',
    margin: 10,
    flexDirection: 'column'
  },
  rowBodyContainer: {
    padding: 15,
    minHeight: 100,
  },
  rowTitle: {
    fontWeight: 'bold'
  },
  logo: {
    width: '20%',
  },
});


export interface ICustomerContractServicesTableState {
}

export interface ICustomerContractServicesTableProps extends WithStyles<typeof styles>{
  customerContractServices: PaginatedDataDTO<ICustomerContractServiceInDTO>;
  tableColumns?: GridColumns<ICustomerContractServiceInDTO>;
  onRowClick?: (customerContractService: ICustomerContractServiceInDTO) => void;
  onFetchElementsOnPage: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onDeleteCustomerContractServiceRequest?: (customerContractService: ICustomerContractServiceInDTO) => void;
  onDuplicateCustomerContractServiceRequest?: (customerContractService: ICustomerContractServiceInDTO) => void;
  rowsPerPage: number;
  rowHeight?: number;
  disableSelectionOnClick?: boolean;
  isLoading?: boolean;
  showCustomer?: boolean;
  hideDeleteButton?: boolean;
  onCustomerClick?: (customerId: number) => void;
  onContractClick?: (contractId: number) => void;
  hideFooter?: boolean;
}

class CustomerContractServicesTable extends React.Component<ICustomerContractServicesTableProps, ICustomerContractServicesTableState> {
  private defaultColumns: GridColumns<ICustomerContractServiceInDTO>;
  public render() {
    const { customerContractServices, hideFooter, showCustomer, hideDeleteButton, rowsPerPage, disableSelectionOnClick, onRowClick, onPageSizeChange, onFetchElementsOnPage, tableColumns, rowHeight, onDeleteCustomerContractServiceRequest, onDuplicateCustomerContractServiceRequest } = this.props;
    this.defaultColumns = [
      {
        field: 'groupFamilySubjectCode',
        headerName: translations.t('credits.groupFamilySubjectCode'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 300,
        flex: 2,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerContractService = params.row;
          return (
            <div>
              <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>{customerContractService.groupFamily || 'N/A'}</div>
              <div style={{ fontSize: 13, marginBottom: 2 }}>{customerContractService.subject || 'N/A'}</div>
              <div style={{ fontSize: 13 }}>{customerContractService.code || 'N/A'}</div>
            </div>
          )}
      },
      ...(showCustomer ? [{
        field: 'contractAndCustomer',
        headerName: translations.t('credits.customerAndContract'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 200,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerContractService = params.row;
          const customerId = customerContractService.customerId;
          const customerName = customerContractService.customerName || 'N/A';
          const contractId = customerContractService.customerContractId;
          const customerContractName = customerContractService.customerContractName || 'N/A';
          const canClickContract = Boolean(contractId && this.props.onContractClick);
          return (
            <div>
              {customerId && customerName ? (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <CustomersIcon style={{ fontSize: '1em', fontWeight: 'bold', color: 'slateblue', marginBottom: 0, paddingBottom: 0 }}/>
                  <h4
                    className={"linkLabel"}
                    style={{ padding: 6, marginLeft: 0, fontSize: 13, fontWeight: 'bold', color: 'slateblue', whiteSpace: 'normal', paddingBottom: 0, marginBottom: 0, marginTop: 0, paddingTop: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      this.props.onCustomerClick(customerId);
                    }}
                  >
                    {customerName}
                  </h4>
                  </div>
              ):null}
              {canClickContract ? (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 6 }}>
                  <CustomerContractIconCustom style={{ width: '1em', height: '1em', color: 'slateblue' }} className="" />
                  <h4
                    className={"linkLabel"}
                    style={{ padding: 6, marginLeft: 0, fontSize: 13, fontWeight: 'bold', color: 'slateblue', whiteSpace: 'normal', paddingBottom: 0, marginBottom: 0, marginTop: 0, paddingTop: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      this.props.onContractClick(contractId);
                    }}
                  >
                    {customerContractName}
                  </h4>
                </div>
              ) : (
                <div style={{ fontSize: 13 }}>{customerContractName}</div>
              )}
            </div>
          )}
      }] : []),
      {
        field: 'sfdcIdAndSelloutPo',
        headerName: translations.t('credits.sfdcIdAndSelloutPo'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerContractService = params.row;
          return (
            <div>
              <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>{translations.t('credits.customerServiceSfdcId')}: {customerContractService.sfdcId || 'N/A'}</div>
              <div style={{ fontSize: 13 }}>{translations.t('credits.customerServiceSelloutPONumber')}: {customerContractService.sellOutPONumber || 'N/A'}</div>
            </div>
          )}
      },
      {
        field: 'activationDeactivationStatus',
        headerName: translations.t('credits.activationDeactivationStatus'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerContractService = params.row;
          const formatDate = (timestamp: number) => {
            if (!timestamp) return 'N/A';
            return new Date(timestamp).toLocaleDateString('it-IT', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          };
          return (
            <div>
              <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>{formatDate(customerContractService.activationDate)} - {formatDate(customerContractService.deactivationDate)}</div>
              <div style={{ fontSize: 13 }}>
                <span style={{ color: getCustomerContractServiceStatusColor(customerContractService.status || ""), fontWeight: 'bold', whiteSpace: 'normal' }}>
                  {translations.t(`credits.customerContractServiceStatus_${customerContractService.status || ''}`).toUpperCase()}
                </span>
              </div>
            </div>
          )}
      },
      {
        field: 'price',
        headerName: translations.t('credits.contractServicePrice'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 120,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerContractService = params.row;
          const price = customerContractService.price;
          const currency = customerContractService.currency;
          
          let displayPrice = 'N/A';
          
          if (price !== null && price !== undefined && !Number.isNaN(price)) {
            const currencySymbol = currency ? Utils.getCurrencyTranslation(currency) : '€';
            displayPrice = `${price}${currencySymbol}`;
          }
          
          return (
            <div style={{ fontSize: 14, fontWeight: 'bold', textAlign: 'center', flex: 1 }}>
              {displayPrice}
            </div>
          );
        }
      },
      ...(!hideDeleteButton && (AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_DELETE) || AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_CREATE)) ? [{
        field: 'actions',
        headerName: translations.t('forms.actions'),
        renderCell: (params: any) => {
          const canDelete = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_DELETE);
          const canCreate = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_CREATE);
          
          return (
            <div style={{ display: 'flex', gap: 10 }}>
              {canCreate && onDuplicateCustomerContractServiceRequest && (
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: '#5AC0B1',
                    flexShrink: 0,
                    width: 'fit-content',
                    color: 'white',
                    padding: 4,
                    paddingLeft: 10,
                    paddingRight: 10,
                    fontWeight: 'bold'
                  }}
                  onClick={(e) => {e.stopPropagation(); onDuplicateCustomerContractServiceRequest(params.row)}}
                >
                  <FileCopy style={{ fontSize: 15 }}/>
                  <h5 style={{ fontWeight: 'bold', margin: 0, marginLeft: 5 }} ><ReduxLanguage languageKey={"forms.duplicate"} /></h5>
                </Button>
              )}
              {canDelete && onDeleteCustomerContractServiceRequest && (
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: 'darkred',
                    flexShrink: 0,
                    width: 'fit-content',
                    color: 'white',
                    padding: 4,
                    paddingLeft: 10,
                    paddingRight: 10,
                    fontWeight: 'bold'
                  }}
                  onClick={(e) => {e.stopPropagation(); onDeleteCustomerContractServiceRequest(params.row)}}
                >
                  <DeleteOutline style={{ fontSize: 15 }}/>
                  <h5 style={{ fontWeight: 'bold', margin: 0, marginLeft: 5 }} ><ReduxLanguage languageKey={"forms.delete"} /></h5>
                </Button>
              )}
            </div>
          )
        },
        type: 'string',
        sortable: false,
        minWidth: 250,
      }] : []),
    ];

    return (
      <DataGrid
        getRowId={(d) => d.id}
        rows={customerContractServices.content || []}
        columns={tableColumns ? tableColumns: this.defaultColumns}
        rowHeight={rowHeight ? rowHeight: 100}
        onPageSizeChange={hideFooter ? null : (pageSize) => onPageSizeChange(pageSize)}
        onPageChange={hideFooter ? null : page => onFetchElementsOnPage(page)}
        page={hideFooter ? null : (customerContractServices?.pagination as PaginationDTO)?.number}
        rowCount={hideFooter ? null : (customerContractServices?.pagination as PaginationDTO)?.totalElements}
        pageSize={hideFooter ? null : rowsPerPage}
        hideFooter={hideFooter || false}
        loading={this.props.isLoading}
        onRowClick={row => onRowClick ? onRowClick(row.row) : null}
        paginationMode="server"
        disableSelectionOnClick={disableSelectionOnClick}
        componentsProps={{
          pagination: {
            labelRowsPerPage: translations.t('forms.rowsPerPage')
          }
        }}
        components={{
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                <SearchOutlined style={{ alignSelf: 'center' }} />
                <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='credits.noCustomerContractServiceFindTitle' /></h3>
                <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='credits.noCustomerContractServiceFindDescription' /></h4>
              </div>
            </Stack>
          ),
          NoResultsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='credits.noCustomerContractServiceFindTitle' /></h3>
              <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='credits.noCustomerContractServiceFindDescription' /></h4>
            </Stack>
          )
        }}
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    );
  }
}

export default withStyles(styles)(CustomerContractServicesTable);